import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, message, Typography, List, Row, Col, Layout, Pagination, Space, Breadcrumb } from 'antd';
import { createOrUpdateBusinessInfoEntry, createOrUpdateQAEntry, getEntries, getKnowledgeDatabase, deleteEntry } from '../KnowledgeDatabaseServices';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import EntryItem from './EntryItem/EntryItem';
import { Content } from 'antd/es/layout/layout';
import DatabaseKPIs from './DatabaseKPIs/DatabaseKPIs';
import NewEntryForm from './NewEntryForm/NewEntryForm';
import { DatabaseOutlined } from '@ant-design/icons';


const { Text, Title } = Typography;

const KnowledgeDatabaseDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { loading, setLoading } = useGlobal();
    const [entries, setEntries] = useState([]);
    const [databaseInfo, setDatabaseInfo] = useState(null);
    const [createEntryLoading, setCreateEntryLoading] = useState(false);
    const [fetchingDatabaseInfo, setFetchingDatabaseInfo] = useState(false);

    const formRef = useRef(null);

    const [paginationMetadata, setPaginationMetadata] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const fetchEntries = useCallback(async (databaseId, page = 1) => {
        try {
            setLoading(true);
            const { data, success, metadata, ...payload } = await getEntries(databaseId, page);
            if (success) {
                setEntries(data);
                setPaginationMetadata({
                    current: metadata.page,
                    pageSize: metadata.page_size,
                    total: metadata.total
                });
            } else {
                const text = resolveBackendMessage(t, payload);
                if (text) message.error(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_fetching_entries'));
        } finally {
            setLoading(false);
        }
    }, []);

    const handlePageChange = (page) => {
        fetchEntries(id, page);
    };

    const fetchDatabaseInfo = useCallback(async (localId, useGlobalLoading = true) => {
        if (!localId) {
            message.error(t('no_database_id_provided'));
            return;
        }
        try {
            if (useGlobalLoading) {
                setLoading(true);
            } else {
                setFetchingDatabaseInfo(true);
            }
            const { data } = await getKnowledgeDatabase(localId);
            setDatabaseInfo(data);
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_fetching_database_information'));
        } finally {
            if (useGlobalLoading) {
                setLoading(false);
            } else {
                setFetchingDatabaseInfo(false);
            }
        }
    }, [setLoading, t]);

    const handleCreateOrUpdateEntry = useCallback(async (values) => {
        if (!id) {
            message.error(t('no_database_id_provided'));
            return;
        }
        try {
            setCreateEntryLoading(true);
            const api = values.entryType === 'qa' ? createOrUpdateQAEntry : createOrUpdateBusinessInfoEntry;
            const entryData = values.entryType === 'qa'
                ? {
                    knowledge_base_id: id,
                    question: values.question,
                    answer: values.answer
                }
                : {
                    knowledge_base_id: id,
                    content: values.content
                };
            // create new entry only; updates happen inline in EntryItem
            const payload = await api(entryData);
            await fetchEntries(id, paginationMetadata.current);
            await fetchDatabaseInfo(id);
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                message.error(t('unauthorized_access'));
            } else {
                message.error(resolveBackendMessage(t, error?.response?.data) || t('error_creating_updating_entry'));
            }
        } finally {
            setCreateEntryLoading(false);
        }
    }, [id,
        fetchEntries, fetchDatabaseInfo, paginationMetadata]);

    const handleInlineUpdate = useCallback(async (entry, values) => {
        try {
            setLoading(true);
            const payload = entry.entry_type === 'question_answer'
                ? await createOrUpdateQAEntry({ id: entry.id, question: values.question, answer: values.answer })
                : await createOrUpdateBusinessInfoEntry({ id: entry.id, content: values.content });
            await fetchEntries(id, paginationMetadata.current);
            await fetchDatabaseInfo(id);
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_updating_entry'));
        } finally {
            setLoading(false);
        }
    }, [id, fetchEntries, fetchDatabaseInfo, paginationMetadata, setLoading, t]);

    const handleDeleteEntry = async (entryId) => {
        try {
            const { success, ...payload } = await deleteEntry(entryId);
            if (success) {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
                const newPage = entries.length === 1 && paginationMetadata.current > 1
                    ? paginationMetadata.current - 1
                    : paginationMetadata.current;
                await fetchEntries(id, newPage);
                await fetchDatabaseInfo(id);
            } else {
                const text = resolveBackendMessage(t, payload);
                if (text) message.error(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_deleting_entry'));
        }
    };

    const handleToggleEntry = async (entry, isDisabled) => {
        try {
            setLoading(true);
            const payload = entry.entry_type === 'question_answer'
                ? await createOrUpdateQAEntry({ id: entry.id, is_disabled: isDisabled })
                : await createOrUpdateBusinessInfoEntry({ id: entry.id, is_disabled: isDisabled });
            await fetchEntries(id, paginationMetadata.current);
            await fetchDatabaseInfo(id);
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_updating_entry'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchDatabaseInfo(id);
            fetchEntries(id);
        }
    }, [id, fetchDatabaseInfo, fetchEntries]);

    if (!id) {
        return (
            <Layout>
                <Content>
                    <Row justify="center">
                        <Col xs={24} md={12}>
                            <Card>
                                <Text type="danger">{t('no_database_id_provided_check_url')}</Text>
                            </Card>
                        </Col>
                    </Row>
                </Content>
            </Layout>
        );
    }
    return (
        <Layout>
            <Content>
                <Row style={{ marginBottom: '8px' }}>
                    <Col span={24}>
                        <Breadcrumb
                            items={[
                                {
                                    title: <Link to="/knowledge-base">
                                        <DatabaseOutlined /> {t('knowledge_bases')}
                                    </Link>,
                                },
                                {
                                    title: databaseInfo?.name || t('loading'),
                                },
                            ]}
                        />
                    </Col>
                </Row>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <DatabaseKPIs databaseInfo={databaseInfo} refresh={() => fetchDatabaseInfo(id, false)} />
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            <NewEntryForm
                                onSubmit={handleCreateOrUpdateEntry}
                                loading={createEntryLoading}
                                formRef={formRef}
                                knowledgeBaseId={id}
                            />
                        </Col>
                        <Col xs={24} md={16}>
                            {entries.length > 0 && (
                                <Card
                                    bordered={false}
                                    style={{
                                        borderRadius: '8px'
                                    }}>
                                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                        <Title level={3} style={{ margin: 0 }}>
                                            {t('existing_entries')}
                                        </Title>
                                        <List
                                            dataSource={entries}
                                            renderItem={item => (
                                                <EntryItem
                                                    key={item?.id}
                                                    item={item}
                                                    onDelete={() => handleDeleteEntry(item.id)}
                                                    onToggle={handleToggleEntry}
                                                    onUpdate={handleInlineUpdate}
                                                    updating={loading}
                                                />
                                            )}
                                        />
                                        {paginationMetadata.total > paginationMetadata.pageSize && (
                                            <Row justify="end">
                                                <Pagination
                                                    current={paginationMetadata.current}
                                                    pageSize={paginationMetadata.pageSize}
                                                    total={paginationMetadata.total}
                                                    onChange={handlePageChange}
                                                    showSizeChanger={false}
                                                />
                                            </Row>
                                        )}
                                    </Space>
                                </Card>
                            )}
                        </Col>
                    </Row>
                </Space>
            </Content>
        </Layout>
    );
};

export default KnowledgeDatabaseDetail;
